import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

/**
 * LE BADGE UNIQUE (LOT-H8).
 *
 * ══════════════════════════════════════════════════════════════════════════
 * POURQUOI CE FICHIER EXISTE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Un badge est la plus petite surface colorée du produit, et c'est justement
 * pour ça qu'elle a proliféré : quinze lignes de `className` suffisaient à en
 * écrire un, donc chaque page en a écrit un. La recette DA a mesuré le
 * résultat :
 *
 *   - **DA-019** — le badge de rôle « Admin » est **VIOLET** sur `/admin/team`,
 *     `/admin/permissions`, `/admin/all-users` et `/settings`, « Agent » bleu,
 *     « Utilisateur » vert. Un rôle n'est ni un succès, ni un avertissement,
 *     ni une erreur : c'est une **donnée**. Il est neutre (§1.2).
 *   - **DA-021** — `/sms/logs` affichait « Envoyé » deux fois par ligne, en
 *     pilule bleue (direction) et en pilule cyan (statut).
 *   - **DA-031** — le badge « Active » du détail campagne, posé dans le cluster
 *     d'actions à la même hauteur et au même rayon que « Mettre en pause »,
 *     **ressemblait à un bouton**.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * CINQ TONS, ET LE TON EST UN RÔLE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Contrat : `docs/design/DESIGN-SYSTEM.md` §1.2.
 *
 * | Ton       | Emploi                                                        |
 * |-----------|---------------------------------------------------------------|
 * | `neutral` | **Défaut.** Fait sans jugement : rôle, direction, catégorie,   |
 * |           | état de cycle de vie (Brouillon, Terminée), « En attente ».    |
 * | `success` | Abouti, délivré, intéressé, actif au sens « ça marche ».       |
 * | `warning` | À surveiller : occupé, non délivré, quota proche.              |
 * | `danger`  | Échec, erreur bloquante. **Rien d'autre.**                     |
 * | `accent`  | Sélection, en cours, valeur mise en avant par l'écran.         |
 *
 * ⚠️ **Le défaut est `neutral`, et ce n'est pas un détail d'API.** Un badge dont
 * le ton doit être choisi explicitement pousse à en choisir un « qui se voit » ;
 * un badge neutre par défaut oblige à justifier la couleur. C'est exactement
 * l'inversion que DA-019 demande — le rôle redevient une donnée parce que la
 * teinte a cessé d'être gratuite.
 *
 * La teinte vit dans le **fond et le texte**, jamais dans une bordure de 2 px :
 * c'est la bordure appuyée qui faisait lire « Active » comme un bouton (DA-031).
 */

export type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'accent'

/**
 * Aplats à 15 % et texte au cran 400 : la recette exacte que le lot H5 avait
 * convergée à la main sur `/calls`, `/sms/logs` et `/emails/logs`. Ce fichier
 * ne l'invente pas, il la rend inévitable.
 */
const TONES: Record<BadgeTone, string> = {
  neutral: 'bg-neutral-500/15 text-foreground-secondary',
  success: 'bg-success-500/15 text-success-400',
  warning: 'bg-warning-500/15 text-warning-400',
  danger: 'bg-danger-500/15 text-danger-400',
  accent: 'bg-primary-500/15 text-primary-400',
}

/**
 * Pastille de couleur, pour les badges dont le libellé seul ne suffit pas à
 * repérer l'état dans une colonne (présence d'agent, notamment). Elle reprend
 * la teinte du ton — jamais une teinte à elle.
 */
const DOTS: Record<BadgeTone, string> = {
  neutral: 'bg-neutral-400',
  success: 'bg-success-500',
  warning: 'bg-warning-500',
  danger: 'bg-danger-500',
  accent: 'bg-primary-500',
}

export interface BadgeProps {
  /** Défaut `neutral` — une couleur se justifie, elle ne se choisit pas par défaut. */
  tone?: BadgeTone
  /** Icône `lucide`, rendue à 12 px à gauche du libellé. */
  icon?: LucideIcon
  /** Pastille de 6 px à gauche du libellé. Exclusive de `icon`. */
  dot?: boolean
  children: ReactNode
  className?: string
}

export function Badge({ tone = 'neutral', icon: Icon, dot = false, children, className }: BadgeProps) {
  return (
    <span
      className={[
        'inline-flex items-center gap-1 whitespace-nowrap rounded-pill px-2 py-0.5 text-t6',
        TONES[tone],
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {Icon ? (
        <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
      ) : dot ? (
        <span className={`h-1.5 w-1.5 shrink-0 rounded-pill ${DOTS[tone]}`} aria-hidden="true" />
      ) : null}
      {children}
    </span>
  )
}

export default Badge
